import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS, apiRequest, getAuthHeaders } from '@/lib/api';

interface TaskAttachment {
  id: number;
  task: number;
  original_filename: string;
  filename: string;
  content_type: string;
  size: number;
  date_created: string;
  file_url: string | null;
  created_by: {
    id: number;
    user: {
      first_name: string;
      last_name: string;
    };
  } | null;
  in_s3: boolean;
}

/**
 * Hook to fetch attachments for a specific task
 * Returns attachments sorted by date (newest first)
 */
export function useTaskAttachments(taskId: number | null) {
  return useQuery({
    queryKey: ['attachments', taskId],
    queryFn: async () => {
      if (!taskId) {
        return [];
      }

      const response = await apiRequest(
        `${API_ENDPOINTS.ATTACHMENTS.LIST}?task=${taskId}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to fetch attachments'
        }));
        throw new Error(errorData.error || 'Failed to fetch attachments');
      }

      return response.json() as Promise<TaskAttachment[]>;
    },
    enabled: !!taskId,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to upload a file to a task
 * Uses FormData for multipart upload
 */
export function useUploadFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, file }: { taskId: number; file: File }) => {
      const formData = new FormData();
      formData.append('task', String(taskId));
      formData.append('file', file);

      // Get auth headers but remove Content-Type (browser will set it with boundary)
      const headers = getAuthHeaders();
      delete headers['Content-Type'];

      const response = await fetch(API_ENDPOINTS.ATTACHMENTS.LIST, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to upload file'
        }));
        throw new Error(errorData.error || 'Failed to upload file');
      }

      return response.json();
    },
    onSuccess: (data, variables) => {
      // Invalidate attachments query to show the new file
      queryClient.invalidateQueries({ queryKey: ['attachments', variables.taskId] });
      // Also invalidate activities since upload creates an activity log
      queryClient.invalidateQueries({ queryKey: ['activities', variables.taskId] });
    },
  });
}

/**
 * Hook to delete an attachment
 * Uses optimistic updates to remove the attachment from UI immediately
 */
export function useDeleteAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ attachmentId, taskId }: { attachmentId: number; taskId: number }) => {
      const response = await apiRequest(
        API_ENDPOINTS.ATTACHMENTS.DELETE(attachmentId),
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to delete attachment'
        }));
        throw new Error(errorData.error || 'Failed to delete attachment');
      }

      // DELETE typically returns 204 No Content, so don't parse JSON
      return { attachmentId, taskId };
    },
    onMutate: async ({ attachmentId, taskId }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['attachments', taskId] });

      // Snapshot previous value
      const previousAttachments = queryClient.getQueryData<TaskAttachment[]>(['attachments', taskId]);

      // Optimistically remove the attachment
      if (previousAttachments) {
        queryClient.setQueryData<TaskAttachment[]>(
          ['attachments', taskId],
          previousAttachments.filter(att => att.id !== attachmentId)
        );
      }

      return { previousAttachments, taskId };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousAttachments) {
        queryClient.setQueryData(['attachments', context.taskId], context.previousAttachments);
      }
    },
    onSettled: (data) => {
      if (data) {
        // Refetch to ensure consistency
        queryClient.invalidateQueries({ queryKey: ['attachments', data.taskId] });
        // Also invalidate activities since delete creates an activity log
        queryClient.invalidateQueries({ queryKey: ['activities', data.taskId] });
      }
    },
  });
}

/**
 * Helper function to download a file attachment
 * Fetches the file with auth, creates a blob URL, and triggers download
 */
export async function downloadAttachment(attachment: TaskAttachment) {
  if (!attachment.file_url) {
    throw new Error('File URL not available');
  }

  try {
    // Fetch file with authentication
    const response = await apiRequest(
      API_ENDPOINTS.ATTACHMENTS.DOWNLOAD(attachment.id)
    );

    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    // Get the blob
    const blob = await response.blob();

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.original_filename || attachment.filename;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
}

/**
 * Helper function to format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
